// frontend/src/lib/api/userService.ts
import { BaseApiService } from './baseService';
import { ApiResponse } from '../httpClient';

export interface User {
  user_id: number;
  username: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  role: 'admin' | 'super_admin' | 'provider' | 'customer';
  is_active: boolean;
  email_verified_at?: string;
  profile_image?: string;
  created_at: string;
  updated_at: string;
  last_login?: string;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  verified?: boolean;
  date_from?: string;
  date_to?: string;
}

export interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  phone?: string;
  role: string;
  is_active: boolean;
}

class UserService extends BaseApiService {
  constructor() {
    super({
      baseEndpoint: '/users',
      timeout: 10000,
    });
  }

  async getUsers(filters: UserFilters = {}): Promise<ApiResponse<any>> {
    return this.get('', { params: filters });
  }

  async getUserById(id: number): Promise<ApiResponse<{ user: User }>> {
    return this.get(`/${id}`);
  }

  async createUser(data: UserFormData): Promise<ApiResponse<{ user: User }>> {
    return this.post('', data);
  }

  async updateUser(id: number, data: Partial<UserFormData>): Promise<ApiResponse<{ user: User }>> {
    return this.put(`/${id}`, data);
  }

  async deleteUser(id: number): Promise<ApiResponse<void>> {
    return this.delete(`/${id}`);
  }

  async toggleUserStatus(id: number): Promise<ApiResponse<{ user: User }>> {
    return this.post(`/${id}/toggle-status`);
  }

  async updateUserRole(id: number, role: string): Promise<ApiResponse<{ user: User }>> {
    return this.post(`/${id}/update-role`, { role });
  }

  async resetUserPassword(id: number): Promise<ApiResponse<any>> {
    return this.post(`/${id}/reset-password`);
  }

  async bulkUpdateUsers(userIds: number[], updates: Partial<UserFormData>): Promise<ApiResponse<any>> {
    return this.post('/bulk-update', { user_ids: userIds, ...updates });
  }

  async bulkDeleteUsers(userIds: number[]): Promise<ApiResponse<any>> {
    return this.post('/bulk-delete', { user_ids: userIds });
  }

  async getUserStats(): Promise<ApiResponse<any>> {
    return this.get('/stats');
  }

  async getUserActivity(userId?: number, filters: any = {}): Promise<ApiResponse<any>> {
    const endpoint = userId ? `/${userId}/activity` : '/activity';
    return this.get(endpoint, { params: filters });
  }

  async getActiveProviders(): Promise<ApiResponse<any>> {
    return this.get('/providers/active');
  }

  async verifyUserEmail(id: number): Promise<ApiResponse<{ user: User }>> {
    return this.post(`/${id}/verify-email`);
  }

  async unverifyUserEmail(id: number): Promise<ApiResponse<{ user: User }>> {
    return this.post(`/${id}/unverify-email`);
  }

  async sendUserNotification(userId: number, title: string, message: string, type?: string): Promise<ApiResponse<any>> {
    return this.post(`/${userId}/notify`, { title, message, type });
  }

  async impersonateUser(userId: number): Promise<ApiResponse<any>> {
    return this.post(`/${userId}/impersonate`);
  }

  async stopImpersonation(): Promise<ApiResponse<any>> {
    return this.post('/stop-impersonation');
  }

  async exportUsers(filters: UserFilters & { format?: 'csv' | 'excel' } = {}): Promise<ApiResponse<any>> {
    return this.get('/export', { params: filters });
  }
}

export const userService = new UserService();